import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { AccountService } from 'src/app/core/service/account.service';

// Component Data Interface
interface ComparisonRow {
  category: string;
  sbf: string;
  cfl: string;
  highlight?: boolean;
}

// API Response Interfaces (matching account service)
interface TableRow {
  row_label: string;
  SBF: string;
  CLF: string;
}

interface OfferOptionsData {
  title_content: string;
  sub_title_content: string;
  table_content: TableRow[];
  special_note_content: string;
  apply_question_content: string;
  apply_question_yes_content: string;
  apply_question_no_content: string;
  disclosure_content: string;
  continue_sbf_btn_content: string;
  continue_clf_btn_content: string;
}

@Component({
  selector: 'app-offers-comparison',
  templateUrl: './offers-comparison.component.html',
  styleUrls: ['./offers-comparison.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class OffersComparisonComponent implements OnInit {

  window = window; // Make window available in template

  // API-driven data properties
  isLoading = true;
  apiError = false;

  // Dynamic content from API
  titleContent = '';
  subTitleContent = '';
  comparisonData: ComparisonRow[] = [];
  specialNoteContent = '';
  applyQuestionContent = '';
  applyQuestionYesContent = '';
  applyQuestionNoContent = '';
  disclosureContent = '';
  continueSbfBtnContent = '';
  continueClfBtnContent = '';



  // Fallback data (sample response as local constant)
  private readonly FALLBACK_DATA: OfferOptionsData = {
    "title_content": "UNDERSTAND YOUR OFFER OPTIONS",
    "sub_title_content": "We have your offer options! In addition to a SBF we also have an alternative loan product from affiliate entity.\n\nBelow explains differences between the products, pros and cons and other details.",
    "table_content": [
      {
        "row_label": "Financing Type",
        "SBF": "Sales-Based Financing <br/> (SBF)",
        "CLF": "Commercial Loan Facility <br/> (CLF)"
      },
      {
        "row_label": "Legal Structure",
        "SBF": "Sale/purchase of future receivables",
        "CLF": "Non-Conditional Debt obligation"
      },
      {
        "row_label": "Repayment Basis",
        "SBF": "Fixed % of future sales/revenue",
        "CLF": "Fixed installments (principal + interest)"
      },
      {
        "row_label": "Payment Amounts",
        "SBF": "Fluctuate with sales volume, flexible but less predictable",
        "CLF": "Fixed regardless of sales"
      },
      {
        "row_label": "Term Length",
        "SBF": "No set term – estimates are provided but actual term depends on sales pace",
        "CLF": "Fixed term and maturity date"
      },
      {
        "row_label": "Total Repayment",
        "SBF": "The amount of future Receipts purchased",
        "CLF": "Principal + precomputed interest"
      },
      {
        "row_label": "Collateral",
        "SBF": "(In Texas scary truths about negotiating DACA with your bank and how difficult it is to convince bank to subordinate)",
        "CLF": "UCC on Accounts and Future receipts"
      },
      {
        "row_label": "Default Scenario",
        "SBF": "If business fails, funder may not collect full amount",
        "CLF": "Business still owes outstanding principal + interest"
      },
      {
        "row_label": "Predictability",
        "SBF": "Lower (uncertain repayment timeline)",
        "CLF": "Higher (fixed amortization schedule)"
      }
    ],
    "special_note_content": "Note: Please select your option. If you want to apply for the loan, your application will be submitted to Revenued VA, and you will be advised on their decision and terms.",
    "apply_question_content": "Would you like to apply for a commercial loan facility?",
    "apply_question_yes_content": "Yes",
    "apply_question_no_content": "No, I want to proceed with SBF",
    "disclosure_content": "Disclosure: I/We hereby apply for the loan or credit described in this application on behalf of the applicant business. I/We certify that I/we made no misrepresentation in this loan application or in any related documents, that all information is true and complete, and that I/we did not omit any important information. I/We agree that any property securing the loan or credit will not be used for any illegal or restricted purposes. Lender is authorized to verify with other parties and to make any investigation of my/our credit, either directly or through any agency employed by Lender. Lender may disclose information as to Lender's experiences or transactions with my/our account as permitted in the Revenued VA LLC's Privacy Policy. I/We understand that Lender will retain this application and any other credit information Lender receives, even if no loan or credit is granted. These representations and authorizations extend not only to Lender, but also to any insurer of the loan and to any investor to whom Lender may sell all or any part of the loan. I/We further authorize Lender to provide any such insurer or investor any information and documentation that they may request with respect to my/our application, credit or loan.",
    "continue_sbf_btn_content": "Continue with SBF",
    "continue_clf_btn_content": "Apply for CLF"
  };

  constructor(private router: Router, private accountService: AccountService) { }

  ngOnInit(): void {
    // Load data from API or fallback to local data
    this.loadOfferOptionsData();
  }

  /**
   * Load offer options data from API with fallback to local data
   */
  private loadOfferOptionsData(): void {
    this.isLoading = true;
    this.apiError = false;

    this.accountService.fetchOfferOptionsFromAPI().subscribe({
      next: (data) => {
        console.log('API data loaded successfully:', data);
        this.mapApiDataToComponent(data);
        this.isLoading = false;
      },
      error: (error) => {
        console.error('API call failed, using fallback data:', error);
        this.apiError = true;
        this.mapApiDataToComponent(this.FALLBACK_DATA);
        this.isLoading = false;
      }
    });
  }



  /**
   * Map API data to component properties
   */
  private mapApiDataToComponent(data: OfferOptionsData): void {
    try {
      // Map basic content
      this.titleContent = data.title_content || 'UNDERSTAND YOUR OFFER OPTIONS';
      this.subTitleContent = data.sub_title_content || '';
      this.specialNoteContent = data.special_note_content || '';
      this.applyQuestionContent = data.apply_question_content || '';
      this.applyQuestionYesContent = data.apply_question_yes_content || 'Yes';
      this.applyQuestionNoContent = data.apply_question_no_content || 'No, I want to proceed with SBF';
      this.disclosureContent = data.disclosure_content || '';
      this.continueSbfBtnContent = data.continue_sbf_btn_content || 'Continue with SBF';
      this.continueClfBtnContent = data.continue_clf_btn_content || 'Apply for CLF';

      // Map table data
      this.comparisonData = this.mapTableData(data.table_content || []);

      console.log('Component data mapped successfully:', {
        titleContent: this.titleContent,
        comparisonRows: this.comparisonData.length,
        specialNote: this.specialNoteContent
      });
    } catch (error) {
      console.error('Error mapping API data:', error);
      // Use fallback data if mapping fails
      this.mapApiDataToComponent(this.FALLBACK_DATA);
    }
  }

  /**
   * Map table content from API to component format
   */
  private mapTableData(tableContent: TableRow[]): ComparisonRow[] {
    return tableContent.map((row, index) => ({
      category: row.row_label,
      sbf: this.processHtmlContent(row.SBF),
      cfl: this.processHtmlContent(row.CLF),
      highlight: row.row_label.toLowerCase().includes('collateral') // Highlight collateral row
    }));
  }

  /**
   * Process HTML content to preserve line breaks and handle HTML tags
   */
  private processHtmlContent(content: string): string {
    if (!content) return '';

    // Preserve <br/> tags and convert to line breaks for display
    // The innerHTML will handle the HTML rendering in the template
    return content.trim();
  }

  /**
   * Checks if the current domain is an offers-options domain
   */
  get isOffersOptionsDomain(): boolean {
    const hostname = window.location.hostname;
    // Remove port number if present for local development
    const cleanHostname = hostname.split(':')[0];
    return cleanHostname === 'offers-options-uat.revenued.com' ||
      cleanHostname === 'offers-options-local.revenued.com' ||
      cleanHostname === 'offers-options.revenued.com' ||
      cleanHostname === 'offers-options-uat.revenuedva.com' ||
      cleanHostname === 'offers-options-local.revenuedva.com' ||
      cleanHostname === 'offers-options.revenuedva.com';
  }

  /**
   * Handles link clicks for pricing rows
   */
  onPricingLinkClick(type: 'sbf' | 'cfl'): void {
    // Handle navigation to respective offer links
    if (type === 'sbf') {
      // Navigate to SBF offer
      console.log('Navigate to SBF offer');
    } else {
      // Navigate to CFL offer
      console.log('Navigate to CFL offer');
    }
  }

  /**
   * Handles the user's selection and proceeds with the application
   */
  onMakeSelection(): void {
    // This could open a modal or navigate to application form
    console.log('User wants to make a selection');
  }


  isOptInChecked: boolean = false;
  selectedMethod: string = ''; // Example selected method, could be 'sms'
  toggleOptInCheckbox(): void {
    this.isOptInChecked = !this.isOptInChecked;
  }
  isButtonDisabled(): boolean {
    if (this.isOptInChecked) return false;
    else return true;
  }
}
